import React, { useCallback } from 'react';
import { FieldLabel } from '@puckeditor/core';

type OpacitySliderProps = {
  field: { label?: string };
  name: string;
  value?: number;
  onChange: (value: number) => void;
  readOnly?: boolean;
};

type OpacitySliderOptions = {
  /** Prefix for the range input's `data-testid` (`<prefix>-<field name>`). */
  testIdPrefix: string;
  /** Value shown when the saved value is missing or not a finite number. */
  fallback: number;
  /** Optional helper text under the slider, also used as its tooltip. */
  hint?: string;
};

/** Clamps a saved value to 0-100, using `fallback` for anything that is not a finite number. */
export function clampPercent(value: unknown, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.min(100, Math.max(0, value));
}

/**
 * Builds the 0-100 percentage slider used by every opacity control (background color opacity,
 * image overlay opacity). One implementation so markup, clamping and accessibility stay in step.
 */
export function createOpacitySlider({
  testIdPrefix,
  fallback,
  hint,
}: OpacitySliderOptions): (props: OpacitySliderProps) => JSX.Element {
  function OpacitySlider({ field, name, value, onChange, readOnly }: OpacitySliderProps): JSX.Element {
    const opacity = clampPercent(value, fallback);
    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => onChange(Number(event.currentTarget.value)),
      [onChange]
    );

    return (
      <FieldLabel label={field.label ?? name} readOnly={readOnly}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} title={hint}>
          <input
            type='range'
            min={0}
            max={100}
            step={1}
            value={opacity}
            onChange={handleChange}
            disabled={readOnly}
            aria-label={field.label ?? name}
            data-testid={`${testIdPrefix}-${name}`}
            style={{ flex: 1 }}
          />
          <span
            style={{
              color: 'var(--dcm-cms-background-opacity-value-color, #646d72)',
              fontSize: 12,
              minWidth: 36,
              textAlign: 'right',
            }}
          >
            {Math.round(opacity)}%
          </span>
        </div>
        {hint && (
          <p
            style={{
              margin: '4px 0 0',
              fontSize: 12,
              color: 'var(--dcm-cms-background-opacity-value-color, #646d72)',
            }}
          >
            {hint}
          </p>
        )}
      </FieldLabel>
    );
  }
  return OpacitySlider;
}
